import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';

import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import styled from 'styled-components';

import { KEYS } from '../../../../../../../../constants/Common';


import { useIntl } from 'react-intl';
import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../../../hooks/connectAuth';

import Button from '../../../../../Common/Form/Button';
import Popover from '../../../../../Common/Form/Popover';
import Select, { Option } from '../../../../../Common/Form/Select';
import { MINUTES, HOURS_24FORMAT } from '../../../../Constants';
import HOURS_US_FORMAT from '../../../../HoursUSFormat';
import { IAuthState } from '../../../../Store/Reducers';
import { IKeyDown, Types } from '../../../../Store/SignUpReducer';


interface ITimePickerProps {
    hour: string,
    minute: string,
    keyDown: IKeyDown,
    useUsFormat: boolean,
    selectedHour: string,
    openTimePicker: boolean,
}

interface ITimePickerAttributes {
    onTimeChanged: (time: string) => void
}

type TTimePickerProps = IConnectedProps<ITimePickerProps> & ITimePickerAttributes;

const KEYS_WHITELIST = [KEYS.ESC, KEYS.ENTER];

const Container = styled.div`
    > p {
        color: ${({ theme }) => theme.colors.black};
        padding: 0 ${({ theme }) => theme.space.mouse};
        font-size: ${({ theme }) => theme.fontSizes.cat};
        margin-bottom: ${({ theme }) => theme.space.mouse};
        font-weight: ${({ theme }) => theme.fontWeights.black};

        > span {
            display: flex;
            align-items: center;
            flex-direction: column;
        }
    }    
`;

const SelectContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.mouse};
`;

const ButtonGroup = styled.div`
    display: flex;
    margin: 0.9375em 0 0;
    gap: ${({ theme }) => theme.space.mouse};

    ${Button} {
        flex: 1;
    }
`;

const TimePicker: React.FC<TTimePickerProps> = ({ dispatch, ...props }) => {
    const intl = useIntl();
    const hourInput = useRef<HTMLButtonElement>(null);
    const [disableCancelButton, setDisableCancelButton] = useState(false);

    const {
        hour,
        minute,
        keyDown,
        useUsFormat,
        selectedHour,
        openTimePicker
    } = props;

    const handleChangeHour = (value: string) => {
        setDisableCancelButton(true);
        dispatch({
            type: Types.CHANGE_TIME_STATE,
            data: { hour: value }
        });

        setTimeout(() => {
            setDisableCancelButton(false);
        }, 300);
    };

    const handleChangeMinute = (value: string) =>{
        setDisableCancelButton(true);
        dispatch({
            type: Types.CHANGE_TIME_STATE,
            data: { minute: value }

        });

        setTimeout(() => {
            setDisableCancelButton(false);
        }, 300);
    };

    const handleClickCancel = useCallback(() => {
        if (!selectedHour) {
            dispatch({
                type: Types.CHANGE_TIME_STATE,
                data: { hour: '', minute: '' }
            });
        }

        dispatch({
            type: Types.CHANGE_HOUR_DATA,
            data: { openTimePicker: false }
        });
    }, [dispatch, selectedHour]);

    const handleClickConfirm = useCallback(() => dispatch({
        type: Types.CHANGE_HOUR_DATA,
        data: {
            error: '',
            openTimePicker: false,
            selectedHour: `${hour}:${minute}`
        }
    }), [hour, minute, dispatch]);

    const handleButtonKeyDown: React.KeyboardEventHandler<HTMLAnchorElement> = ({ target, key }) => {
        if (key !== KEYS.ENTER) return;

        if ((target as HTMLAnchorElement).id === 'time-selector-cancel') {
            handleClickCancel();
            return;
        }

        if ((target as HTMLAnchorElement).id !== 'time-selector-confirm') return;

        handleClickConfirm();
    };

    const hourOptions = useMemo(() => {
        if (useUsFormat) {
            return HOURS_US_FORMAT.map((item) => (
                <Option value={item.value} key={item.value}>
                    {item.label}
                </Option>
            ));
        }

        return HOURS_24FORMAT.map((item) => (<Option value={item} key={item}>{item}</Option>));
    }, [useUsFormat]);

    const minuteOptions = useMemo(() => MINUTES.map((item) => <Option value={item} key={item}>{item}</Option>), []);

    useEffect(() => {
        if (!keyDown || keyDown && !keyDown.key) return;

        const hasFocus = document.getElementById('popover-time-picker')?.contains(document.activeElement);

        if (!hasFocus) dispatch({ type: Types.RESET_KEYDOW });

        if (!(hasFocus && openTimePicker)) return;

        if (keyDown.key === KEYS.ESC) {
            handleClickCancel();
            return;
        }

        const buttonCancel = document.getElementById('cancel-time-picker');
        const buttonConfirm = document.getElementById('confirm-time-picker');

        if (document.activeElement === buttonConfirm) {
            handleClickConfirm();
            return;
        }

        if (document.activeElement !== buttonCancel) return;

        handleClickCancel();
    }, [keyDown, openTimePicker, dispatch, handleClickCancel, handleClickConfirm]);

    return (
        <Popover
            modal
            id='time-picker'
            open={openTimePicker}
        >
            <Container data-cy="auth-time-picker">
                <p><FormattedHTMLMessage id="authFunctional.timeSelector.title" /></p>

                <SelectContainer>
                    <Select
                        value={hour}
                        id="birth-hour"
                        ref={hourInput}
                        onValueChange={handleChangeHour}
                        tabIndex={openTimePicker ? 0 : -1}
                        placeholder={intl.formatMessage({ id: 'authFunctional.timeSelector.hour' })}
                    >
                        {hourOptions}
                    </Select>

                    <Select
                        value={minute}
                        id="birth-minute"
                        disabled={hour === ''}
                        onValueChange={handleChangeMinute}
                        tabIndex={openTimePicker ? 0 : -1}
                        placeholder={intl.formatMessage({ id: 'authFunctional.timeSelector.minute' })}
                    >
                        {minuteOptions}
                    </Select>
                </SelectContainer>

                <ButtonGroup>
                    <Button
                        variation="outlined"
                        id="cancel-time-picker"
                        onClick={handleClickCancel}
                        onKeyDown={handleButtonKeyDown}
                        tabIndex={openTimePicker ? 0 : -1}
                        disabled={disableCancelButton}
                    >
                        <FormattedMessage id="authFunctional.modalButtons.cancel" />
                    </Button>

                    <Button
                        disabled={!minute}
                        id="confirm-time-picker"
                        onClick={handleClickConfirm}
                        onKeyDown={handleButtonKeyDown}
                        tabIndex={(minute && openTimePicker) ? 0 : -1}
                    >
                        <FormattedMessage id="authFunctional.modalButtons.continue" />
                    </Button>
                </ButtonGroup>
            </Container>
        </Popover>
    );
};

const mapStateToProps: IAuthMapStateToProps<ITimePickerProps> = (state, isComparison = false) => (!isComparison)
    ? {
        useUsFormat: (state as IAuthState).Container.i18n.useUSFormat,
        hour: (state as IAuthState).SignUp.birthData.birthHour.timeState.hour,
        minute: (state as IAuthState).SignUp.birthData.birthHour.timeState.minute,
        selectedHour: (state as IAuthState).SignUp.birthData.birthHour.selectedHour,
        openTimePicker: (state as IAuthState).SignUp.birthData.birthHour.openTimePicker,
        keyDown: (KEYS_WHITELIST.indexOf((state as IAuthState).SignUp.keyDown.key) !== -1) ? (state as IAuthState).SignUp.keyDown : undefined
    } : {
        hour: state.hour,
        minute: state.minute,
        keyDown: state.keyDown,
        useUsFormat: state.useUsFormat,
        selectedHour: state.selectedHour,
        openTimePicker: state.openTimePicker
    };

export default connectAuth<ITimePickerProps, ITimePickerAttributes>(mapStateToProps)(TimePicker);
